import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { createFile, setHat } from "../api.js";
import { invalidate } from "../data/useFetch.js";
import { usePage, useTree } from "../data/usePage.js";
import { useRepo, repoRelativePath, reposOnShow } from "../data/repo.js";
import { HeaderSearch } from "./Search.js";
import type { SessionInfo } from "../../shared/types.js";

function VersionBadge() {
  const [version, setVersion] = useState<string | null>(null);

  useEffect(() => {
    fetch("/healthz")
      .then((res) => res.json())
      .then((data) => setVersion(data.version))
      .catch(() => {});
  }, []);

  if (!version) return null;
  // Portal to <body>: the header's backdrop-filter makes it the containing
  // block for fixed descendants, which would pin the badge to the header
  // instead of the viewport.
  return createPortal(<span id="dw-version">{version}</span>, document.body);
}

export function Header({
  session,
  filesOpen,
  onToggleFiles,
}: {
  session: SessionInfo;
  /** Is the file-tree drawer showing? (Narrow windows only — see App.tsx.) */
  filesOpen: boolean;
  onToggleFiles: () => void;
}) {
  const loc = useLocation();
  const navigate = useNavigate();
  const repo = useRepo();
  const [hatOpen, setHatOpen] = useState(false);
  const menuRef = useRef<HTMLSpanElement>(null);

  // Dismiss the hat menu on a click anywhere outside it, or on Escape.
  //
  // This used to be an invisible `position: fixed; inset: 0` backdrop div, which
  // silently covered only the HEADER: the header sets `backdrop-filter`, making
  // it the containing block for its fixed descendants (same reason VersionBadge
  // portals to <body>). So "click anywhere else" worked only inside the header
  // strip. A listener has no geometry to get wrong. `pointerdown`, not `click`,
  // so the menu is gone before the press lands on whatever is underneath.
  useEffect(() => {
    if (!hatOpen) return;
    const onDown = (e: PointerEvent): void => {
      if (!menuRef.current?.contains(e.target as Node)) setHatOpen(false);
    };
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") setHatOpen(false);
    };
    document.addEventListener("pointerdown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("pointerdown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [hatOpen]);

  // Switching hats changes the access identity, so refetch session + content.
  const applyHat = async (action: () => Promise<void>): Promise<void> => {
    try {
      await action();
      invalidate("session");
      invalidate("tree");
      invalidate("page:");
    } catch (e) {
      window.alert(e instanceof Error ? e.message : String(e));
    }
    setHatOpen(false);
  };
  // Repo-relative path of the current page (the URL minus its `/<repo>` prefix).
  const path = repoRelativePath(loc.pathname, repo);
  const user = session.user;
  // Content routes (not the `_…` system views) carry per-path access. Read the
  // current page's `can` from the shared cache (the page is already fetched by
  // PageView) so edit affordances appear only where the user may actually edit.
  const onContent = !loc.pathname.replace(/^\/+/, "").startsWith("_");
  const page = usePage(path, onContent);
  const canEdit =
    page.status === "ready" && page.data.kind !== "none" && page.data.can.includes("edit");
  // The repo's own root ops (already fetched for the sidebar). The page `can`
  // above covers content routes only, so on a system route like `/_repo/dwdocs`
  // this is the check that keeps "new file…" off a read-only repo.
  const tree = useTree(repo);
  const repoCanEdit = tree.status === "ready" && tree.data.can.includes("edit");

  // Create a new empty file (uncommitted draft) and open it. The default name
  // carries the current file's extension — markdown URLs drop their `.md`, so
  // an absent URL extension means `.md`. The file is created as a sibling of
  // the current path (or at the repo root from `/`).
  const onNewFile = async (): Promise<void> => {
    const ext = /\.[^./]+$/.exec(path)?.[0] ?? ".md";
    const dir = path.includes("/") ? path.replace(/\/[^/]*$/, "") : "";
    const name = window.prompt("New file name:", `New file${ext}`);
    if (!name) return;
    const target = name.includes("/") ? name : dir ? `${dir}/${name}` : name;
    try {
      const out = await createFile(repo, target);
      invalidate("tree");
      invalidate("commit");
      invalidate("session");
      // Every file opens inline via ?edit: markdown (and any other text file)
      // gets the source overlay over the rendered page; a query file shows its
      // live result below the editor. Markdown URLs drop their `.md`.
      navigate(`/${repo}/${out.path.replace(/\.(md|markdown)$/, "")}?edit`);
    } catch (e) {
      window.alert(e instanceof Error ? e.message : String(e));
    }
  };

  return (
    <header className="dw-header">
      {/* The file tree's handle once the window is too narrow to hold it as a
          column; CSS hides it at full width, where the tree is always there. */}
      <button
        type="button"
        className="dw-drawer-btn"
        aria-expanded={filesOpen}
        aria-controls="dw-files"
        title={filesOpen ? "close files" : "files"}
        onClick={onToggleFiles}
      >
        {filesOpen ? "×" : "☰"}
      </button>
      <Link className="dw-brand" to="/">
        {session.logo ? (
          // A configured logo replaces the icon + wordmark entirely.
          <img
            className="dw-logo"
            src={`/appearance/logo?v=${encodeURIComponent(session.logo)}`}
            alt={session.brand}
          />
        ) : (
          <>
            {session.icon && (
              <img
                className="dw-brand-icon"
                src={`/favicon.svg?v=${encodeURIComponent(session.icon)}`}
                alt=""
              />
            )}
            {session.brand}
          </>
        )}
        {session.local && <span className="dw-badge">local</span>}
      </Link>
      {/* Search covers exactly the repos the sidebar is showing — inside a hidden
          repo that's just that repo, so /dwdocs/ searches dwdocs and platform
          docs never dilute a search of your own content. */}
      <HeaderSearch repos={reposOnShow(session.repos, repo).map((r) => r.name)} />
      <nav className="dw-nav">
        {/* `from` lets Tools offer "share this page" for the page you were on. */}
        <Link className="dw-link" to="/_tools" state={{ from: loc.pathname }}>
          tools
        </Link>
        {session.remote &&
          (session.remote.loggedIn ? (
            <form method="post" action="/oauth/logout" className="dw-inline">
              <button className="dw-link" type="submit" title={session.remote.url}>
                data ✓
              </button>
            </form>
          ) : (
            <a className="dw-link" href="/oauth/login" title={session.remote.url}>
              connect data
            </a>
          ))}
        {session.editing && repoCanEdit && (!onContent || canEdit) && (
          <button className="dw-link" type="button" onClick={onNewFile}>
            new file…
          </button>
        )}
        {session.authProvider === "local" && !session.local && (
          <span
            className="dw-auth-local"
            title="auth.provider is local — anyone can sign in as anyone. For development and trusted networks only."
          >
            auth: local
          </span>
        )}
        {user ? (
          <span className="dw-user-menu" ref={menuRef}>
            {/* A picture is target enough — the name and hat head the menu it
                opens, rather than spending header width on every page (`title`
                for a hover, too). Without a picture the name IS the button. */}
            <button
              type="button"
              className="dw-user"
              aria-haspopup="true"
              aria-expanded={hatOpen}
              title={session.hat ? `${user.name} /${session.hat}` : user.name}
              onClick={() => setHatOpen((o) => !o)}
            >
              {user.avatarUrl ? (
                <img
                  src={user.avatarUrl}
                  alt=""
                  width={22}
                  height={22}
                  referrerPolicy="no-referrer"
                />
              ) : (
                <>
                  {user.name}
                  {session.hat && <span className="dw-hat">/{session.hat}</span>}
                </>
              )}
            </button>
            {hatOpen && (
              <div className="dw-hat-menu">
                <div className="dw-hat-who">
                  {user.name}
                  {session.hat && <span className="dw-hat">/{session.hat}</span>}
                </div>
                <button
                  type="button"
                  disabled={!session.hat}
                  onClick={() => void applyHat(() => setHat(null))}
                >
                  No hat
                </button>
                {session.hats.map((h) => (
                  <button
                    key={h}
                    type="button"
                    disabled={session.hat === h}
                    onClick={() => void applyHat(() => setHat(h))}
                  >
                    /{h}
                  </button>
                ))}
                <Link to="/_hats" onClick={() => setHatOpen(false)}>
                  Manage hats…
                </Link>
                <form method="post" action="/auth/logout">
                  <button className="dw-hat-signout" type="submit">
                    sign out
                  </button>
                </form>
              </div>
            )}
          </span>
        ) : (
          <a
            className="dw-link"
            href={`/auth/login?redirect=${encodeURIComponent(loc.pathname)}`}
          >
            sign in
          </a>
        )}
        <VersionBadge />
      </nav>
    </header>
  );
}
